import { getChatCompletion } from './aiIntegrations/chatCompletion';
import { supabase } from '../lib/supabase';

/**
 * Generate image annotations using OpenAI Vision GPT-4o
 * Returns bounding boxes, arrows, and labels for regions of interest
 */
export async function generateImageAnnotations(imageBase64, imageDescription, caseContext = {}) {
  const systemPrompt = `You are a fire forensics expert identifying key evidence in scene photographs. 

Generate annotation coordinates for:
- Fire origin points (red boxes with "Origin Point" label)
- Burn patterns and fire progression (yellow arrows with descriptive labels)
- Critical evidence items (blue boxes with specific labels)
- Structural damage areas (orange boxes with damage type labels)

Rules:
- Use normalized coordinates (0-1 scale) where 0,0 is top-left and 1,1 is bottom-right
- For boxes: provide x, y (top-left corner), width, height
- For arrows: provide startX, startY, endX, endY
- Limit to 5-8 most critical annotations per image
- Use clear, concise labels (max 4 words)
- Confidence threshold: only annotate clearly visible evidence

Output JSON array of annotations.`;

  const userPrompt = `Analyze this fire scene image and generate annotations for key forensic evidence.

Image Description: ${imageDescription}

Case Context:
- Property Type: ${caseContext?.propertyType || 'Unknown'}
- Allegation: ${caseContext?.allegationType || 'Unknown'}

Identify and annotate:
1. Probable fire origin area
2. Burn pattern directions
3. Critical evidence (electrical, ignition sources, accelerants)
4. Structural damage zones

Return JSON array with type, coordinates, color, and label for each annotation.`;

  try {
    const response = await getChatCompletion(
      'OPEN_AI',
      'gpt-4o',
      [
        {
          role: 'user',
          content: [
            { type: 'text', text: userPrompt },
            {
              type: 'image_url',
              image_url: {
                url: imageBase64,
                detail: 'high'
              }
            }
          ]
        }
      ],
      {
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'ImageAnnotations',
            schema: {
              type: 'object',
              properties: {
                annotations: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      type: { type: 'string', enum: ['box', 'arrow', 'label'] },
                      label: { type: 'string' },
                      color: { type: 'string' },
                      confidence: { type: 'number' },
                      x: { type: 'number' },
                      y: { type: 'number' },
                      width: { type: 'number' },
                      height: { type: 'number' },
                      startX: { type: 'number' },
                      startY: { type: 'number' },
                      endX: { type: 'number' },
                      endY: { type: 'number' }
                    },
                    required: ['type', 'label', 'color'],
                    additionalProperties: false
                  }
                }
              },
              required: ['annotations'],
              additionalProperties: false
            },
            strict: true
          }
        },
        max_completion_tokens: 1500
      }
    );

    const content = response?.choices?.[0]?.message?.content;
    const annotationData = JSON.parse(content);

    return {
      success: true,
      data: annotationData?.annotations || [],
      usage: response?.usage
    };
  } catch (error) {
    console.error('Annotation generation error:', error);
    return {
      success: false,
      error: error?.message || 'Failed to generate annotations'
    };
  }
}

/**
 * Save annotations to database
 */
export async function saveAnnotations(imageId, annotations) {
  try {
    if (!annotations || annotations?.length === 0) {
      return { success: true, data: [] };
    }

    const annotationsData = annotations?.map(ann => ({
      image_id: imageId,
      annotation_type: ann?.type,
      label: ann?.label,
      color: ann?.color,
      confidence: ann?.confidence || 0.8,
      x: ann?.x || null,
      y: ann?.y || null,
      width: ann?.width || null,
      height: ann?.height || null,
      start_x: ann?.startX || null,
      start_y: ann?.startY || null,
      end_x: ann?.endX || null,
      end_y: ann?.endY || null
    }));

    const { data, error } = await supabase?.from('annotations')?.insert(annotationsData)?.select();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Save annotations error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get annotations for an image
 */
export async function getImageAnnotations(imageId) {
  try {
    const { data, error } = await supabase?.from('annotations')?.select('*')?.eq('image_id', imageId)?.order('created_at', { ascending: true });

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Get annotations error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Render annotations on canvas
 */
export function renderAnnotationsOnCanvas(canvas, annotations, imageWidth, imageHeight) {
  if (!canvas || !annotations || annotations?.length === 0) return;

  const ctx = canvas?.getContext('2d');
  ctx?.clearRect(0, 0, canvas?.width, canvas?.height);

  annotations?.forEach(annotation => {
    ctx.strokeStyle = annotation?.color || '#DC2626';
    ctx.fillStyle = annotation?.color || '#DC2626';
    ctx.lineWidth = 3;
    ctx.font = 'bold 14px Inter, sans-serif';

    if (annotation?.annotation_type === 'box' || annotation?.type === 'box') {
      // Draw bounding box
      const x = (annotation?.x || 0) * imageWidth;
      const y = (annotation?.y || 0) * imageHeight;
      const width = (annotation?.width || 0) * imageWidth;
      const height = (annotation?.height || 0) * imageHeight;

      ctx?.strokeRect(x, y, width, height);

      // Draw label background
      if (annotation?.label) {
        const labelY = y > 25 ? y - 8 : y + height + 20;
        const textMetrics = ctx?.measureText(annotation?.label);
        const labelWidth = textMetrics?.width + 12;
        
        ctx.fillStyle = annotation?.color || '#DC2626';
        ctx?.fillRect(x, labelY - 16, labelWidth, 20);
        
        ctx.fillStyle = '#FFFFFF';
        ctx?.fillText(annotation?.label, x + 6, labelY);
      }
    } else if (annotation?.annotation_type === 'arrow' || annotation?.type === 'arrow') {
      // Draw arrow
      const startX = (annotation?.start_x || annotation?.startX || 0) * imageWidth;
      const startY = (annotation?.start_y || annotation?.startY || 0) * imageHeight;
      const endX = (annotation?.end_x || annotation?.endX || 0) * imageWidth;
      const endY = (annotation?.end_y || annotation?.endY || 0) * imageHeight;

      ctx?.beginPath();
      ctx?.moveTo(startX, startY);
      ctx?.lineTo(endX, endY);
      ctx?.stroke();

      // Draw arrowhead
      const angle = Math.atan2(endY - startY, endX - startX);
      const arrowSize = 15;
      
      ctx?.beginPath();
      ctx?.moveTo(endX, endY);
      ctx?.lineTo(
        endX - arrowSize * Math.cos(angle - Math.PI / 6),
        endY - arrowSize * Math.sin(angle - Math.PI / 6)
      );
      ctx?.lineTo(
        endX - arrowSize * Math.cos(angle + Math.PI / 6),
        endY - arrowSize * Math.sin(angle + Math.PI / 6)
      );
      ctx?.closePath();
      ctx?.fill();

      // Draw label
      if (annotation?.label) {
        const labelX = endX + 10;
        const labelY = endY - 10;
        const textMetrics = ctx?.measureText(annotation?.label);
        const labelWidth = textMetrics?.width + 12;
        
        ctx.fillStyle = annotation?.color || '#DC2626';
        ctx?.fillRect(labelX, labelY - 16, labelWidth, 20);
        
        ctx.fillStyle = '#FFFFFF';
        ctx?.fillText(annotation?.label, labelX + 6, labelY);
      }
    }
  });
}

/**
 * Generate annotated image file (canvas to blob)
 */
export async function generateAnnotatedImageFile(originalImageUrl, annotations) {
  try {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        
        renderAnnotationsOnCanvas(canvas, annotations, img.width, img.height);
        
        canvas.toBlob((blob) => {
          if (blob) {
            resolve({ success: true, blob });
          } else {
            reject(new Error('Failed to generate blob'));
          }
        }, 'image/jpeg', 0.95);
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = originalImageUrl;
    });
  } catch (error) {
    console.error('Generate annotated image error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Upload annotated image to storage
 */
export async function uploadAnnotatedImage(userId, caseId, imageId, annotatedBlob) {
  try {
    const { uploadToStorage } = await import('./storageService');
    
    const fileName = `${userId}/${caseId}/annotated_${imageId}_${Date.now()}.jpg`;
    const uploadResult = await uploadToStorage('annotated-images', fileName, annotatedBlob);
    
    if (!uploadResult?.success) {
      throw new Error(uploadResult?.error);
    }

    // Update case_images with annotated path
    const { error } = await supabase?.from('case_images')?.update({ annotated_image_url: uploadResult?.data?.path })?.eq('id', imageId);

    if (error) throw error;

    return { success: true, path: uploadResult?.data?.path };
  } catch (error) {
    console.error('Upload annotated image error:', error);
    return { success: false, error: error?.message };
  }
}